import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, filter, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';

import {
  BootstrapNotificationsData,
  BootstrapResult,
  BootstrapSettingsData,
  BootstrapUserData,
  BootstrapUserPlanData,
} from '@app/core/types/bootstrap-result.interface';

import { AppBootstrapApiService } from './app-bootstrap-api.service';
import { StorageService, StorageType } from './storage.service';


const BOOTSTRAP_STORAGE_KEY = 'app_bootstrap';

@Injectable({
  providedIn: 'root',
})
export class AppBootstrapService {
  private readonly api = inject(AppBootstrapApiService);
  private readonly storage = inject(StorageService);

  readonly data = signal<BootstrapResult | null>(null);
  readonly loading = signal(false);

  readonly userData = computed(() => this.data()?.userData ?? null);
  readonly notificationsData = computed(() => this.data()?.notificationsData ?? null);
  readonly settingsData = computed(() => this.data()?.settingsData ?? null);
  readonly banks = computed(() => this.data()?.banks ?? []);
  readonly helpItems = computed(() => this.data()?.helpItems ?? []);
  readonly userPlanData = computed(() => this.data()?.userPlanData ?? null);

  private sessionLoaded = false;
  private inflight$: Observable<boolean> | null = null;

  constructor() {
    this.hydrateFromStorage();
  }

  ensureLoaded(force = false): Observable<boolean> {
    if (!force && this.sessionLoaded && this.data()) {
      return of(true);
    }

    if (this.inflight$) {
      return this.inflight$;
    }

    this.loading.set(true);

    this.inflight$ = this.api.getBootstrap().pipe(
      filter(response => response.success),
      map(response => response.result),
      tap(result => this.setData(result)),
      map(() => true),
      catchError(() => of(false)),
      finalize(() => {
        this.loading.set(false);
        this.inflight$ = null;
      }),
      shareReplay(1),
    );

    return this.inflight$;
  }

  refresh(): Observable<boolean> {
    return this.ensureLoaded(true);
  }

  setData(data: BootstrapResult): void {
    this.data.set(data);
    this.sessionLoaded = true;
    this.persist(data);
  }

  updateUserData(partial: Partial<BootstrapUserData>): void {
    this.patch(current => ({
      ...current,
      userData: { ...current.userData, ...partial },
    }));
  }

  updateNotificationsData(partial: Partial<BootstrapNotificationsData>): void {
    this.patch(current => ({
      ...current,
      notificationsData: { ...current.notificationsData, ...partial },
    }));
  }

  updateSettingsData(partial: Partial<BootstrapSettingsData>): void {
    this.patch(current => ({
      ...current,
      settingsData: { ...current.settingsData, ...partial },
    }));
  }

  updateUserPlanData(partial: Partial<BootstrapUserPlanData>): void {
    this.patch(current => ({
      ...current,
      userPlanData: { ...current.userPlanData, ...partial },
    }));
  }

  clear(): void {
    this.data.set(null);
    this.sessionLoaded = false;
    this.inflight$ = null;
    this.storage.removeItem(BOOTSTRAP_STORAGE_KEY, StorageType.SESSION);
  }

  private patch(mutator: (current: BootstrapResult) => BootstrapResult): void {
    const current = this.data();

    if (!current) {
      return;
    }

    this.setData(mutator(current));
  }

  private hydrateFromStorage(): void {
    const cached = this.storage.getItem(BOOTSTRAP_STORAGE_KEY, StorageType.SESSION);

    if (!cached) {
      return;
    }

    try {
      this.data.set(JSON.parse(cached) as BootstrapResult);
    } catch {
      this.storage.removeItem(BOOTSTRAP_STORAGE_KEY, StorageType.SESSION);
    }
  }

  private persist(data: BootstrapResult): void {
    this.storage.setItem(BOOTSTRAP_STORAGE_KEY, JSON.stringify(data), StorageType.SESSION);
  }
}
